// P2 presigned PUT helper — Content-Type only; tags are in the signed URL query.

export const MUSIC_UPLOAD_CONTENT_TYPE = "audio/mpeg";
export const MUSIC_UPLOAD_MAX_BYTES = 100 * 1024 * 1024;
export const MUSIC_UPLOAD_MIN_BYTES = 1024;

export function validateMp3File(file: File): string | null {
  const isMp3 =
    file.type === MUSIC_UPLOAD_CONTENT_TYPE ||
    file.name.toLowerCase().endsWith(".mp3");
  if (!isMp3) {
    return "Only MP3 files (audio/mpeg) are supported.";
  }
  if (file.size < MUSIC_UPLOAD_MIN_BYTES) {
    return `File must be at least ${MUSIC_UPLOAD_MIN_BYTES} bytes.`;
  }
  if (file.size > MUSIC_UPLOAD_MAX_BYTES) {
    return `File exceeds the ${Math.floor(MUSIC_UPLOAD_MAX_BYTES / (1024 * 1024))} MB limit.`;
  }
  return null;
}

/**
 * DUP-ui — compute the file's SHA-256 in the browser (WebCrypto). This is an
 * advisory HINT only: the server recomputes the authoritative checksum at
 * upload receipt. Fail-open by design — any hashing failure (old browser,
 * memory pressure, non-secure context) returns null and the upload proceeds
 * without init-time checksum duplicate detection.
 */
export async function computeFileSha256Hex(file: File): Promise<string | null> {
  try {
    if (!window.crypto?.subtle?.digest) return null;
    const buffer = await file.arrayBuffer();
    const digest = await window.crypto.subtle.digest("SHA-256", buffer);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

export function putFileToPresignedUrl(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`Upload to storage failed (HTTP ${xhr.status})`));
    });
    xhr.addEventListener("error", () => {
      reject(new Error("Network error during file upload"));
    });
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.send(file);
  });
}
